import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Modal } from "./Modal";
import { clients as clientsApi, type CsvImportResult } from "../lib/api";

export function CsvImportDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Batch tag + record type so a migrated set stays identifiable in the roster.
  const [sourceTag, setSourceTag] = useState("");
  const [recordType, setRecordType] = useState<"client" | "lead">("client");
  const inputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setFile(null); setBusy(false); setResult(null); setError(null); setSourceTag(""); setRecordType("client");
  }

  async function submit() {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const res = await clientsApi.importCsv(file, {
        source: sourceTag.trim() || undefined,
        // Leads shouldn't land in the roster looking like active clients.
        status: recordType === "lead" ? "pending" : "onboarding",
      });
      setResult(res);
      onDone();
    } catch (e: any) {
      setError(e?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import clients from CSV or Excel"
      footer={
        result ? (
          <button className="primary-button" onClick={() => { reset(); onClose(); }}>Done</button>
        ) : (
          <>
            <button className="ghost-button" onClick={() => { reset(); onClose(); }}>Cancel</button>
            <button className="primary-button" disabled={!file || busy} onClick={submit}>
              {busy ? "Uploading…" : "Import"}
            </button>
          </>
        )
      }
    >
      {result ? (
        <ImportSummary result={result} />
      ) : (
        <>
          {/* Batch tag + type: keeps a migrated set identifiable (e.g. everything
              brought over from Dispute Fox) and stops leads landing in the
              roster as if they were active clients. */}
          <div className="stacked-form" style={{ marginBottom: 12 }}>
            <label htmlFor="import-type">These records are</label>
            <select
              id="import-type"
              value={recordType}
              onChange={(e) => setRecordType(e.target.value as "client" | "lead")}
            >
              <option value="client">Clients</option>
              <option value="lead">Leads</option>
            </select>
            <label htmlFor="import-tag">Tag this batch (optional)</label>
            <input
              id="import-tag"
              type="text"
              placeholder={recordType === "lead" ? "Dispute Fox — Leads" : "Dispute Fox — Clients"}
              value={sourceTag}
              onChange={(e) => setSourceTag(e.target.value)}
            />
            <span className="muted small">
              Shows as the client's source so you can tell this batch apart later.
            </span>
          </div>
          <div
            className="dropzone"
            role="button"
            tabIndex={0}
            aria-label="Click or drop a CSV or Excel file to import"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
          >
            <Upload size={24} aria-hidden="true" />
            <strong>{file ? file.name : "Drop a CSV or Excel file here or click to choose"}</strong>
            <span className="muted small">
              Columns: legal_name, email, phone, address_line1, address_city, address_state, address_zip
            </span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          {error && <p className="error-text">{error}</p>}
        </>
      )}
    </Modal>
  );
}

// Post-import outcome: a plain-language headline, a breakdown of WHY rows were
// skipped (grouped by category), and — for anything that failed — the specific
// reasons grouped so a 300-row import doesn't print 280 near-identical lines.
function ImportSummary({ result }: { result: CsvImportResult }) {
  const skippedTotal = result.skipped ?? result.errors.length;
  const breakdown: Array<{ n: number; label: string }> = [
    { n: result.duplicates_in_db ?? 0, label: "already in your account" },
    { n: result.duplicates_in_file ?? 0, label: "duplicate in the file" },
    { n: result.skipped_missing_data ?? 0, label: "missing a name or email" },
    { n: result.invalid_rows ?? 0, label: "had an invalid email" },
    { n: result.skipped_by_plan_cap ?? 0, label: "over your plan limit" },
    { n: result.row_errors ?? 0, label: "could not be saved" },
  ].filter((b) => b.n > 0);

  // Group the per-row detail by reason so identical reasons collapse to one line.
  const byReason = new Map<string, number[]>();
  for (const e of result.errors) {
    const list = byReason.get(e.reason) ?? [];
    list.push(e.row);
    byReason.set(e.reason, list);
  }
  const grouped = [...byReason.entries()].sort((a, b) => b[1].length - a[1].length);

  return (
    <div>
      <p>
        <strong>{result.created}</strong> client{result.created === 1 ? "" : "s"} imported
        {skippedTotal > 0 ? `, ${skippedTotal} row${skippedTotal === 1 ? "" : "s"} skipped.` : "."}
      </p>

      {breakdown.length > 0 && (
        <ul className="muted small" style={{ margin: "0.25rem 0 0.5rem", paddingLeft: "1.1rem" }}>
          {breakdown.map((b, i) => (
            <li key={i}>{b.n} {b.label}</li>
          ))}
        </ul>
      )}

      {grouped.length > 0 && (
        <>
          <p className="error-text" style={{ marginBottom: "0.25rem" }}>Skipped rows:</p>
          <ul className="error-list">
            {grouped.map(([reason, rowsForReason], i) => {
              const shown = rowsForReason.slice(0, 10).join(", ");
              const more = rowsForReason.length > 10 ? ` +${rowsForReason.length - 10} more` : "";
              return (
                <li key={i}>
                  {rowsForReason.length > 1 ? `${rowsForReason.length}× ` : ""}
                  {reason} <span className="muted small">(row{rowsForReason.length === 1 ? "" : "s"} {shown}{more})</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
